/**
 * Utilidades de los gráficos (paleta, escalas y etiquetas).
 *
 * Los componentes de `components/charts` son SVG puros y sin dependencias: los
 * datos que envía el backend (`{ labels, series, format }`) son simples series
 * numéricas, así que no hace falta una librería de charts y el bundle se mantiene
 * liviano. Todos respetan los tokens de color de la marca.
 */

/** Paleta de series, alineada con los tokens de Tailwind v4. */
export const CHART_COLORS = [
  '#06b6d4', // brand-500
  '#14b8a6', // accent-500
  '#4d7a93', // ink-500
  '#f59e0b', // amber-500
  '#10b981', // emerald-500
  '#f43f5e', // rose-500
  '#8b5cf6', // violet-500
  '#0ea5e9', // sky-500
  '#84cc16', // lime-500
  '#64748b', // slate-500
] as const;

export const chartColor = (index: number): string =>
  CHART_COLORS[index % CHART_COLORS.length] ?? CHART_COLORS[0];

export interface ChartSeries {
  label: string;
  data: (number | null)[];
  /**
   * Color explícito de la serie (ej. el color del producto en la base).
   * Si no se indica, se usa la paleta de la marca por índice.
   */
  color?: string;
}

export const toFinite = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/** Rango vertical de una serie (incluye negativos y ceros). */
export function computeExtent(series: readonly ChartSeries[]): { min: number; max: number } {
  let min = 0;
  let max = 0;

  for (const item of series) {
    for (const raw of item.data) {
      const value = toFinite(raw);
      if (value === null) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }

  if (min === 0 && max === 0) return { min: 0, max: 1 };

  return { min, max };
}

/** Redondea el máximo del eje a un valor "bonito" (1, 2, 2.5, 5 × 10^n). */
export function niceCeil(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;

  const exponent = Math.floor(Math.log10(value));
  const magnitude = 10 ** exponent;
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;

  return step * magnitude;
}

/** Genera las marcas del eje vertical. */
export function buildTicks(min: number, max: number, count = 4): number[] {
  const ticks: number[] = [];
  const step = (max - min) / count;

  if (step <= 0) return [0, 1];

  for (let index = 0; index <= count; index += 1) {
    ticks.push(Number((min + step * index).toFixed(6)));
  }

  return ticks;
}

/** Acorta etiquetas largas del eje horizontal (fechas, nombres de combustible…). */
export function shortenLabel(label: unknown, max = 14): string {
  const text = String(label ?? '');

  if (text.length <= max) return text;

  return `${text.slice(0, max - 1)}…`;
}

/**
 * Etiqueta del eje X: las fechas ISO (`2026-08-13`) se muestran como `13/08`,
 * mucho más legibles y sin riesgo de recortarse.
 */
export function formatAxisLabel(label: unknown, max = 12): string {
  const text = String(label ?? '');
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);

  if (iso) return `${iso[3] ?? ''}/${iso[2] ?? ''}`;

  return shortenLabel(text, max);
}

/**
 * Índices de etiquetas que se muestran en el eje X, para no amontonar texto.
 * Siempre incluye la primera y la última.
 */
export function labelIndexes(total: number, maxLabels: number): number[] {
  if (total <= maxLabels) return Array.from({ length: total }, (_, index) => index);

  const step = (total - 1) / (maxLabels - 1);
  const indexes = new Set<number>();

  for (let index = 0; index < maxLabels; index += 1) {
    indexes.add(Math.round(index * step));
  }

  return [...indexes].sort((a, b) => a - b);
}

/** Construye un trazado SVG a partir de puntos, respetando los huecos (null). */
export function buildLinePath(
  points: { x: number; y: number | null }[],
  spanGaps: boolean,
): { path: string; gaps: boolean } {
  let path = '';
  let gaps = false;
  let pendingMove = true;

  for (const point of points) {
    if (point.y === null) {
      if (!spanGaps) pendingMove = true;
      else gaps = true;
      continue;
    }

    path += `${pendingMove ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)} `;
    pendingMove = false;
  }

  return { path: path.trim(), gaps };
}

// ------------------------------------------------------------------
// Geometría compartida por line-chart y bar-chart
// ------------------------------------------------------------------

export interface ChartGeometry {
  width: number;
  height: number;
  padding: { top: number; right: number; bottom: number; left: number };
  innerWidth: number;
  innerHeight: number;
  min: number;
  max: number;
  /** Línea base (donde y = 0). */
  zeroY: number;
  ticks: { value: number; y: number }[];
  /** Posición X del centro de la banda `index` (barras). */
  bandCenter: (index: number) => number;
  /** Ancho disponible por banda (barras). */
  bandWidth: number;
  /** Posición X del punto `index` (líneas). */
  pointX: (index: number) => number;
  /** Posición Y de un valor. */
  valueY: (value: number) => number;
}

interface BuildGeometryOptions {
  labelCount: number;
  series: readonly ChartSeries[];
  width: number;
  height: number;
  tickCount?: number;
}

/** Calcula escalas y marcas para un gráfico cartesiano. */
export function buildGeometry({
  labelCount,
  series,
  width,
  height,
  tickCount = 4,
}: BuildGeometryOptions): ChartGeometry {
  const padding = { top: 14, right: 14, bottom: 30, left: 54 };
  const innerWidth = Math.max(width - padding.left - padding.right, 10);
  const innerHeight = Math.max(height - padding.top - padding.bottom, 10);

  const extent = computeExtent(series);
  const max = extent.max > 0 ? niceCeil(extent.max) : 0;
  const min = extent.min < 0 ? -niceCeil(Math.abs(extent.min)) : 0;

  const ticks = buildTicks(min, max, tickCount).map((value) => ({
    value,
    y: padding.top + innerHeight - ((value - min) / (max - min || 1)) * innerHeight,
  }));

  const zeroY = padding.top + innerHeight - ((0 - min) / (max - min || 1)) * innerHeight;
  const safeLabels = Math.max(labelCount, 1);
  const bandWidth = innerWidth / safeLabels;

  return {
    width,
    height,
    padding,
    innerWidth,
    innerHeight,
    min,
    max,
    zeroY,
    ticks,
    bandCenter: (index) => padding.left + bandWidth * (index + 0.5),
    bandWidth,
    pointX: (index) => padding.left + (innerWidth * index) / Math.max(safeLabels - 1, 1),
    valueY: (value) => padding.top + innerHeight - ((value - min) / (max - min || 1)) * innerHeight,
  };
}
